import { Dialog, Disclosure } from '@headlessui/react';
import { Switch } from '@headlessui/react';
import {
  ExclamationTriangleIcon,
  XMarkIcon,
  ChevronUpIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';
import { useState, useEffect } from 'react';
import { dateToString, parseSectionSlotDateTime } from '@youshift/shared/utils';
import { useUnassignSlotMutation } from '@youshift/shared/hooks/mutations';
import { ShiftAssignment, ShiftAssignmentType } from '@youshift/shared/types';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import React from 'react';

import { YSButton } from '../Buttons';
import { useItrContext } from '../../layouts/IterationRootLayout/IterationRootLayout';
import { useManagerContext } from '../../layouts/ManagerLayout';
import { iconTypes } from '../../utils/constants';

interface BulkDeleteShiftAssignmentsModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  description: string;
  isLoading?: boolean;
}

export default function BulkDeleteShiftAssignmentsModal({
  isOpen,
  onClose,
  title,
  description,
  isLoading,
}: BulkDeleteShiftAssignmentsModalProps) {
  const { t } = useTranslation();
  const { idItr } = useParams();
  const queryClient = useQueryClient();
  const { epa, sectionSlotsDict, sectionsWithSlots } = useItrContext();
  const { users } = useManagerContext();
  const [inputText, setInputText] = useState('');
  const [selectedShiftAssignments, setSelectedShiftAssignments] = useState<
    ShiftAssignment[]
  >([]);
  const [includeAlgorithmAssignments, setIncludeAlgorithmAssignments] =
    useState(true);
  const [includeManagerAssignments, setIncludeManagerAssignments] =
    useState(false);
  const [includeUserAssignments, setIncludeUserAssignments] = useState(false);

  const handleToggleAssignmentType = (type: ShiftAssignmentType) => {
    switch (type) {
      case ShiftAssignmentType.ALGORITHM:
        setIncludeAlgorithmAssignments(!includeAlgorithmAssignments);
        break;
      case ShiftAssignmentType.MANAGER:
        setIncludeManagerAssignments(!includeManagerAssignments);
        break;
      case ShiftAssignmentType.USER:
        setIncludeUserAssignments(!includeUserAssignments);
        break;
      default:
        break;
    }
  };

  const isAssignmentTypeEnabled = (type: ShiftAssignmentType) => {
    switch (type) {
      case ShiftAssignmentType.ALGORITHM:
        return includeAlgorithmAssignments;
      case ShiftAssignmentType.MANAGER:
        return includeManagerAssignments;
      case ShiftAssignmentType.USER:
        return includeUserAssignments;
      default:
        return false;
    }
  };

  useEffect(() => {
    // Get all shift assignments from all users
    const allAssignments = Object.values(epa.assignmentsMap.byUser).flat();

    const filteredAssignments = allAssignments.filter(assignment => {
      if (assignment.type === ShiftAssignmentType.ALGORITHM) {
        return includeAlgorithmAssignments;
      }
      if (assignment.type === ShiftAssignmentType.MANAGER) {
        return includeManagerAssignments;
      }
      if (assignment.type === ShiftAssignmentType.USER) {
        return includeUserAssignments;
      }
      return true; // Keep other types of assignments
    });

    setSelectedShiftAssignments(filteredAssignments);
  }, [
    includeAlgorithmAssignments,
    includeManagerAssignments,
    includeUserAssignments,
    epa.assignmentsMap.byUser,
  ]);

  const unassignSlotMutation = useUnassignSlotMutation(queryClient, {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shiftAssignments', idItr] });
      onClose();
    },
  });

  const handleDelete = () => {
    const confirmationText = t('manager.shiftAssignment.confirmDeleteShifts', {
      count: selectedShiftAssignments.length,
    });
    if (inputText !== confirmationText) {
      toast.error(t('generic.confirmationTextError'));
      return;
    }
    unassignSlotMutation.mutate({
      id_itr: Number(idItr),
      id_shift_assignments: selectedShiftAssignments.map(
        sa => sa.id_shift_assignment,
      ),
    });
  };

  const sectionsDict = sectionsWithSlots.reduce(
    (acc, { section }) => {
      acc[section.id_section] = section;
      return acc;
    },
    {} as Record<number, { id_section: number; name: string }>,
  );

  // Group assignments by section and sort by date
  const groupedAssignments = selectedShiftAssignments.reduce(
    (acc, assignment) => {
      const sectionSlot = sectionSlotsDict[assignment.id_section_slot];
      const section = sectionsDict[assignment.id_section];

      if (!acc[section.id_section]) {
        acc[section.id_section] = {
          name: section.name,
          assignments: [],
        };
      }

      acc[section.id_section].assignments.push({
        assignment,
        sectionSlot,
        start: new Date(sectionSlot.start),
        end: new Date(sectionSlot.end),
      });

      return acc;
    },
    {} as Record<
      number,
      {
        name: string;
        assignments: {
          assignment: ShiftAssignment;
          sectionSlot: (typeof sectionSlotsDict)[number];
          start: Date;
          end: Date;
        }[];
      }
    >,
  );

  // Sort assignments within each section by date
  Object.values(groupedAssignments).forEach(section => {
    section.assignments.sort((a, b) => a.start.getTime() - b.start.getTime());
  });

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      <Dialog.Backdrop className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />

      <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-4xl sm:p-6">
            <div className="absolute right-0 top-0 hidden pr-4 pt-4 sm:block">
              <button
                type="button"
                className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                onClick={onClose}
              >
                <span className="sr-only">{t('generic.close')}</span>
                <XMarkIcon className="h-6 w-6" aria-hidden="true" />
              </button>
            </div>

            <div className="sm:flex sm:items-start">
              <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                <ExclamationTriangleIcon
                  className="h-6 w-6 text-red-600"
                  aria-hidden="true"
                />
              </div>
              <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                <Dialog.Title
                  as="h3"
                  className="text-base font-semibold leading-6 text-gray-900"
                >
                  {title}
                </Dialog.Title>
                <div className="mt-2">
                  <p className="text-sm text-gray-500">{description}</p>
                </div>

                <div className="mt-4">
                  <div className="flex flex-wrap items-center gap-2">
                    {Object.entries(iconTypes).map(
                      ([type, { bgColor, Icon }]) => {
                        const assignmentType = type as ShiftAssignmentType;
                        const isEnabled =
                          isAssignmentTypeEnabled(assignmentType);
                        return (
                          <button
                            key={type}
                            onClick={() =>
                              handleToggleAssignmentType(assignmentType)
                            }
                            className={`flex items-center gap-2 px-3 py-1.5 rounded-full border transition-colors ${
                              isEnabled
                                ? 'bg-blue-100 border-blue-300 text-blue-800 hover:bg-blue-200'
                                : 'bg-gray-100 border-gray-200 text-gray-500 hover:bg-gray-200'
                            }`}
                          >
                            <div className={`${bgColor} p-1 rounded-full`}>
                              {React.createElement(Icon, {
                                className: 'h-3 w-3 text-white',
                                'aria-label': t(`generic.assignedBy${type}`),
                              })}
                            </div>
                            <span className="text-sm font-medium">
                              {t(`generic.assignedBy${type}`)}
                            </span>
                          </button>
                        );
                      },
                    )}
                  </div>
                </div>

                <div className="mt-4">
                  <p className="text-sm font-medium text-gray-900">
                    {selectedShiftAssignments.length > 0 ? (
                      t('manager.shiftAssignment.shiftAssignmentsToDelete', {
                        count: selectedShiftAssignments.length,
                      })
                    ) : (
                      <span className="text-gray-500">
                        {t('manager.shiftAssignment.noShiftsSelected')}
                        <br />
                        <span className="text-sm">
                          {t('generic.useTogglesToSelect')}
                        </span>
                      </span>
                    )}
                  </p>
                </div>

                <div className="mt-4 space-y-4">
                  {Object.entries(groupedAssignments).map(
                    ([sectionId, section]) => (
                      <Disclosure key={sectionId}>
                        {({ open }) => (
                          <div className="border rounded-lg overflow-hidden">
                            <Disclosure.Button className="flex w-full justify-between items-center px-4 py-3 text-left bg-gray-50 hover:bg-gray-100">
                              <div className="flex items-center">
                                <h4 className="text-lg font-semibold text-gray-900">
                                  {section.name}
                                </h4>
                                <span className="ml-2 text-sm text-gray-500">
                                  ({section.assignments.length}{' '}
                                  {t('generic.shifts')})
                                </span>
                              </div>
                              {open ? (
                                <ChevronUpIcon className="h-5 w-5 text-gray-500" />
                              ) : (
                                <ChevronDownIcon className="h-5 w-5 text-gray-500" />
                              )}
                            </Disclosure.Button>
                            <Disclosure.Panel className="px-4 py-3 space-y-2">
                              {section.assignments.map(
                                ({ assignment, sectionSlot }) => {
                                  const user = users[assignment.id_user];
                                  return (
                                    <div
                                      key={assignment.id_shift_assignment}
                                      className="flex items-center justify-between py-2 border-b last:border-b-0"
                                    >
                                      <div className="text-sm text-gray-900">
                                        {parseSectionSlotDateTime(
                                          sectionSlot.start,
                                          sectionSlot.end,
                                          'en',
                                          false,
                                        )}
                                      </div>
                                      <div className="flex items-center gap-2">
                                        <div className="text-sm text-gray-900">
                                          {user
                                            ? `${user.firstname} ${user.lastname}`
                                            : t('generic.unknownUser')}
                                        </div>
                                        <div className="text-sm text-gray-500">
                                          {assignment.type && (
                                            <div
                                              className={`${iconTypes[assignment.type].bgColor} p-1 rounded-full`}
                                            >
                                              {React.createElement(
                                                iconTypes[assignment.type].Icon,
                                                {
                                                  className:
                                                    'h-3 w-3 text-white',
                                                  'aria-label': t(
                                                    `itrChecks.${assignment.type.toLowerCase()}Assignment`,
                                                  ),
                                                },
                                              )}
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  );
                                },
                              )}
                            </Disclosure.Panel>
                          </div>
                        )}
                      </Disclosure>
                    ),
                  )}
                </div>
              </div>
            </div>

            {selectedShiftAssignments.length > 0 && (
              <>
                <div className="mt-6">
                  <p className="text-sm text-gray-500">
                    {t('generic.typeToConfirm')}
                    <br />
                    <span className="font-bold text-center">
                      {t('manager.shiftAssignment.confirmDeleteShifts', {
                        count: selectedShiftAssignments.length,
                      })}
                    </span>
                  </p>
                </div>
                <div className="mt-2">
                  <input
                    placeholder={t(
                      'manager.shiftAssignment.confirmDeleteShifts',
                      {
                        count: selectedShiftAssignments.length,
                      },
                    )}
                    className="block w-full max-w-4xl rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6"
                    type="text"
                    onChange={e => setInputText(e.currentTarget.value)}
                  />
                </div>
              </>
            )}

            <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
              <YSButton
                variant="delete"
                onClick={handleDelete}
                classNames="sm:ml-3"
                loading={isLoading || unassignSlotMutation.isPending}
                disabled={
                  selectedShiftAssignments.length === 0 ||
                  isLoading ||
                  unassignSlotMutation.isPending
                }
              >
                {t('generic.delete')}
              </YSButton>
              <YSButton
                variant="ghost-secondary"
                onClick={onClose}
                disabled={isLoading || unassignSlotMutation.isPending}
              >
                {t('generic.cancel')}
              </YSButton>
            </div>
          </Dialog.Panel>
        </div>
      </div>
    </Dialog>
  );
}
